import { defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";
import { report } from "@chatfood/bug-reporter";
import { useDirectBackend } from "#imports";
import type { SalesChannel } from "~/domain/sales-channels";

type ToggleName =
  | "allow-guest-checkout"
  | "collect-order-notes"
  | "collect-item-notes"
  | "display-items-out-of-stock"
  | "display-home-menu-link"
  | "display-loyalty-menu-link"
  | "display-order-menu-link"
  | "display-locations-menu-link"
  | "enable-inline-items-cross-sell"
  | "enable-floating-items-cross-sell"
  | "enable-reorder"
  | "skip-auth-flow"
  | "display-order-status-progress";

// Define the state interface
type IState = {
  isLoaded: boolean;
  toggles: {
    name: ToggleName;
    isEnabled: boolean;
  }[];
};

// Helper function to create the initial state
function createInitialState(): IState {
  return {
    isLoaded: false,
    toggles: [],
  };
}

export const useFeatureMatrixStore = defineStore("featureMatrix", () => {
  // Create a reactive state object using the initial state
  const state = reactive<IState>(createInitialState());

  // Helper function to reset the state to its initial values
  function reset() {
    Object.assign(state, createInitialState());
  }

  // Helper function to patch the state with a partial object
  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  const { businessRepo } = useDirectBackend();

  const load = async (businessId: string, salesChannel: SalesChannel) => {
    try {
      const toggles = (await businessRepo.getFeatureMatrixToggles({
        businessId,
        salesChannel,
        // TODO: once we move this store the a package we can type it
        // correctly from a single place and remove this type assertion
      })) as IState["toggles"];

      patch({
        isLoaded: true,
        toggles,
      });
    } catch (error) {
      report(error);
    }
  };

  const isToggleEnabled = (
    toggleName: ToggleName,
    defaultValue = false,
  ): boolean => {
    return (
      state.toggles.find((toggle) => toggle.name === toggleName)?.isEnabled ??
      defaultValue
    );
  };

  const skipAuthFlow = computed(
    () =>
      isToggleEnabled("skip-auth-flow") &&
      isToggleEnabled("allow-guest-checkout"),
  );

  return {
    ...toRefs(state),
    state: computed(() => state),
    patch,
    reset,
    load,
    isToggleEnabled,
    skipAuthFlow,
  };
});
